/* eslint-disable @typescript-eslint/no-explicit-any */
import { RpcError, StatusCode } from "grpc-web";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

// Hooks
import { useAuth } from "../hooks/useAuth";
import useClaimsToast from "../hooks/useToast";

// Protos
import { LegalBenefit as BffLegalBenefit } from "../utils/api/protos/messages/benefits.message_pb";
import { Case, ClaimsLampCase } from "../utils/api/protos/messages/cases.message_pb";
import { ClaimPerson, DocumentCategory } from "../utils/api/protos/messages/claims.message_pb";

// Utils
import { createCoreFile } from "../utils/api/files";
import { getClaimsLampCase } from "../utils/api/cases";

// Utils
import { getLegalBenefitById, listLegalBenefits } from "../utils/api/benefits";
import { getCase } from "../utils/api/cases";
import {
	deleteService,
	getClaim,
	postAssociateDocsChunk,
	postDisassociateDocs,
	postSubmitClaim,
	upsertClaim,
	removeDocument,
	recoverDocument,
	permanentlyRemoveDocument
} from "../utils/api/claims";
import { getPolicyIdByMemberId } from "../utils/api/members";
import {
	convertBffClaimToLocalClaim,
	convertBffLegalBenefitToLocalLegalBenefit,
	initEmptyClaim,
	validateFileSize,
	validateClaimSize
} from "../utils/helpers";
import {
	getServiceLabelFn,
	handleClaimUpsertUtil,
	handleError,
	handleFetchCaseUtil,
	updateBenefitsUtil,
	getCleanErrorMessage
} from "../utils/provider/claims-provider-util";
import {
	Claim,
	LegalBenefit,
	RemoveDocumentResult,
	RecoverDocumentResult,
	PermanentlyRemoveDocumentResult,
	ClaimServiceBillingType
} from "../utils/types";
import { ClaimStatus } from "../utils/consts";

// Export the interface so it can be imported by other components
export interface ClaimContextType {
	/** The current claim saved to the server, other than initial value */
	claim: Claim;
	/** The local working copy of the claim that will be submitted to the server */
	localClaim: Claim;
	benefits: LegalBenefit[];
	localBenefits: LegalBenefit[];
	benefitsList: BffLegalBenefit[];
	isLoading: boolean;
	lgPolicyId?: number;
	claimCase: Case | undefined;
	pendingDeletions: Set<string>;
	billHourlyAsFlat: boolean;
	billFlatAsHourly: boolean;
	doesLegalMatterExist: boolean;
	ccsGenerationStatus: "idle" | "loading" | "success" | "error";
	ccsGenerationError: string | null;
	setIsLoading: (loading: boolean) => void;
	setLgPolicyId: (policyId: number) => void;
	setClaim: (claim: Claim | ((prev: Claim) => Claim)) => void;
	setLocalClaim: (claim: Claim | ((prev: Claim) => Claim)) => void;
	setBillHourlyAsFlat: (billHourlyAsFlat: boolean) => void;
	setBillFlatAsHourly: (billFlatAsHourly: boolean) => void;
	setCcsGenerationStatus: (status: "idle" | "loading" | "success" | "error") => void;
	setCcsGenerationError: (error: string | null) => void;
	handleFetchClaim: (claimId: number, options?: { preserveFields?: Partial<Claim> }) => Promise<Claim | undefined>;
	handleFetchCase: (caseId: number) => Promise<Case | undefined>;
	handleClaimUpsert: (updatedClaim: Claim, updateBilling: boolean) => Promise<boolean>;
	handleDocumentAssociation: (
		bytes: Uint8Array | null,
		newFile: File,
		category: DocumentCategory,
		onProgress?: (progress: number) => void
	) => Promise<void>;
	handleDocumentDisassociation: (fileId: string) => Promise<void>;
	handleSubmitClaim: (receivedOn: Date | undefined) => Promise<void>;
	handleServiceDelete: (claimId: number, serviceId: number) => Promise<void>;
	getServiceLabel: (claimService: any) => string;
	handleRemoveDocument: (claimId: number, fileId: string) => Promise<RemoveDocumentResult>;
	handleRecoverDocument: (claimId: number, fileId: string) => Promise<RecoverDocumentResult>;
	handlePermanentlyRemoveDocument: (claimId: number, fileId: string) => Promise<PermanentlyRemoveDocumentResult>;
}

interface ClaimProviderProps {
	children: React.ReactNode;
}

// Export the ClaimContext so it can be imported by other components
export const ClaimContext = createContext<ClaimContextType | undefined>(undefined);

export const ClaimProvider: React.FC<ClaimProviderProps> = ({ children }) => {
	const { getUser, handleJwtRefresh } = useAuth();
	const [claim, setClaim] = useState<Claim>(initEmptyClaim());
	const [localClaim, setLocalClaim] = useState<Claim>(claim);
	const [claimCase, setClaimCase] = useState<Case | undefined>();
	const [benefits, setBenefits] = useState<LegalBenefit[]>([]);
	const [benefitsList, setBenefitsList] = useState<BffLegalBenefit[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [lgPolicyId, setLgPolicyId] = useState<number>();
	const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(new Set());
	const [billHourlyAsFlat, setBillHourlyAsFlat] = useState<boolean>(false);
	const [billFlatAsHourly, setBillFlatAsHourly] = useState<boolean>(false);
	const [doesLegalMatterExist, setDoesLegalMatterExist] = useState<boolean>(true); //If exists in LAMP
	const [ccsGenerationStatus, setCcsGenerationStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
	const [ccsGenerationError, setCcsGenerationError] = useState<string | null>(null);
	const toast = useClaimsToast();
	const { caseId } = useParams();
	const user = getUser();

	/** First pass initialization */
	useEffect(() => {
		async function injectCaseData(claimCaseId: number) {
			const claimCase = await handleFetchCase(claimCaseId);
			const lampCase = await handleFetchClaimsLampCase(claimCaseId);
			if (claimCase && user && lampCase) {
				setDoesLegalMatterExist(!!lampCase.getLegalmatterstate());

				const claimMember = new ClaimPerson();
				claimMember.setId(claimCase.getMemberid());
				claimMember.setName(claimCase.getClientnamefirst() + " " + claimCase.getClientnamelast());
				const claimAtty = new ClaimPerson();
				claimAtty.setId(parseInt(user.externalId));
				claimAtty.setName(user.name);
				setLocalClaim({
					...claim,
					caseId: claimCase.getCaseid(),
					lampCaseId: lampCase.getLampcaseid(),
					member: claimMember,
					atty: claimAtty,
					legalMatterState: lampCase.getLegalmatterstate() || undefined
				});
			}
		}
		if (caseId && parseInt(caseId)) {
			injectCaseData(parseInt(caseId));
		}
	}, []);

	// Define fetchBenefit so that updateBenefitsUtil can get and convert a benefit.
	const fetchBenefit = async (
		legalBenefitId: number,
		attyId?: number,
		caseId?: number
	): Promise<LegalBenefit | undefined> => {
		try {
			const result = await getLegalBenefitById(legalBenefitId, attyId, caseId);
			const benefit = result.getBenefit();
			if (benefit) {
				return convertBffLegalBenefitToLocalLegalBenefit(benefit);
			}
		} catch (error) {
			handleError(
				toast,
				error as RpcError,
				"There was a problem loading benefit data",
				`There was a problem fetching the LegalBenefit: ${legalBenefitId}\n`
			);
		}
		return undefined;
	};

	/** When `claim` changes, update legal benefits and fetch policy/benefits list if needed */
	useEffect(() => {
		async function updateBenefitsInternal() {
			if (claim.claimId && claim.claimServices.length > 0) {
				// Use the shared updateBenefitsUtil function
				const updatedBenefits = await updateBenefitsUtil(claim, benefits, fetchBenefit);
				setBenefits(updatedBenefits);
			}
		}
		updateBenefitsInternal();

		// Fetch policy and benefits if possible
		if (claim && claim.member && !lgPolicyId) {
			handleFetchPolicyId(claim);
		}
		if (lgPolicyId && !benefitsList.length) {
			handleFetchBenefitsList(lgPolicyId, claim?.atty?.getId(), claim?.caseId);
		}
	}, [claim]);

	/** When `localClaim` changes, update the local legal benefits */
	const localBenefits = useMemo(() => {
		// Fetch policy and benefits if possible
		if (benefitsList && localClaim) {
			const localBenefitCodes = localClaim.claimServices
				.filter((service) => service.legalBenefitId)
				.map((service) => service.legalBenefitId);
			return benefitsList
				.filter((b: BffLegalBenefit) => localBenefitCodes.includes(b.getId()))
				.map((benefit) => convertBffLegalBenefitToLocalLegalBenefit(benefit));
		}
		return [];
	}, [localClaim, benefitsList]);

	// Shared methods

	const handleFetchClaim = async (claimId: number, options?: { preserveFields?: Partial<Claim> }) => {
		setIsLoading(true);
		try {
			const result = await getClaim(claimId);
			const responseClaim = result.getClaim();

			if (responseClaim) {
				const submittedClaim = convertBffClaimToLocalClaim(responseClaim);
				setClaim(submittedClaim);
				if (options?.preserveFields) {
					// Server data as base, with specified local fields overriding (ex. billing data)
					setLocalClaim({ ...submittedClaim, ...options.preserveFields });
				} else {
					setLocalClaim(submittedClaim);
				}
				return submittedClaim;
			}
		} catch (error) {
			handleError(toast, error as RpcError, "There was a problem processing the Claim", "Error fetching claim");
		} finally {
			setIsLoading(false);
		}
	};

	const handleFetchPolicyId = async (claim: Claim) => {
		setIsLoading(true);
		try {
			if (claim.member) {
				const result = await getPolicyIdByMemberId(
					claim.member?.getId(),
					claim.dateOfInitialConsultation,
					claim.finalDateOfService,
					claim?.caseId
				);
				setLgPolicyId(result.getLgPolicyId());
				if (!benefitsList.length) {
					handleFetchBenefitsList(result.getLgPolicyId(), claim.atty?.getId(), claim.caseId);
				}
			}
		} catch (error) {
			handleError(
				toast,
				error as RpcError,
				"There was a problem fetching the member's policy",
				"Error fetching policy"
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleFetchBenefitsList = async (lgPolicyId: number, attyId?: number, caseId?: number) => {
		setIsLoading(true);
		try {
			const result = await listLegalBenefits(lgPolicyId, attyId, caseId);
			const masterList = result.getBenefitsList();
			// Hide member direct-billed benefits: the legacy "M"-suffix codes plus any code the
			// benefits service flags non-billable (isBillable=false, e.g. HCA codes 1-15).
			const filteredList = masterList.filter(
				(benefit) => !benefit.getMatterBillingCode().endsWith("M") && benefit.getIsBillable()
			);

			setBenefitsList(filteredList);
		} catch (error) {
			handleError(
				toast,
				error as RpcError,
				"There was a problem fetching the benefits list",
				"Error fetching benefits"
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleClaimUpsert = async (updatedClaim: Claim, updateBilling: boolean) => {
		setIsLoading(true);
		try {
			if (updateBilling) {
				// If billing hourly rate as flat, add type conversion before submitting
				if (billHourlyAsFlat) {
					updatedClaim.claimServices = updatedClaim.claimServices.map((svc, index) => ({
						...svc,
						billing: updatedClaim.claimServices[index].billing?.map((billing) => ({
							...billing,
							type: ClaimServiceBillingType.Flat
						}))
					}));
				}
				const submittedClaim = await handleClaimUpsertUtil(updatedClaim, upsertClaim);
				if (submittedClaim) {
					setClaim(submittedClaim);
					setLocalClaim(submittedClaim);
				}
			} else {
				const {
					billingAdditionalFees,
					billingtotal,
					otherNonCoveredFees,
					otherNonCoveredHours,
					...claimWithoutBillingFields
				} = updatedClaim;

				const noBillingClaim: Claim = {
					...claimWithoutBillingFields,
					claimServices: updatedClaim.claimServices.map(({ billing, ...svcWithoutBilling }) => ({
						...svcWithoutBilling
					}))
				};

				const submittedClaim = await handleClaimUpsertUtil(noBillingClaim, upsertClaim);
				if (submittedClaim) {
					const billingClaim = {
						...submittedClaim,
						claimServices: submittedClaim.claimServices.map((svc, index) => ({
							...svc,
							billing: updatedClaim.claimServices[index].billing
						})),
						otherNonCoveredFees: updatedClaim.otherNonCoveredFees,
						otherNonCoveredHours: updatedClaim.otherNonCoveredHours
					};
					setClaim(billingClaim);
					setLocalClaim(billingClaim);
				}
			}
			return true;
		} catch (error: any) {
			console.error(error);
			// gRPC errors carry the server-side reason on `.message` (e.g. a date change
			// that would alter the policy). Surface that string rather than the raw object.
			toast("Error", error?.message ?? "Updating claim failed", "error");
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	const handleServiceDelete = async (claimId: number, serviceId: number) => {
		setIsLoading(true);
		try {
			await deleteService(claimId, serviceId);
			const updatedClaim = {
				...claim,
				claimServices: claim.claimServices.filter((service) => service.serviceId !== serviceId)
			};
			setClaim(updatedClaim);
			setLocalClaim(updatedClaim);
		} catch (error) {
			handleError(toast, error as RpcError, "Failed to delete service", "Error deleting service");
		} finally {
			setIsLoading(false);
		}
	};

	// State for file upload progress
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [currentUploadFile, setCurrentUploadFile] = useState<string | null>(null);

	const handleDocumentAssociation = async (
		bytes: Uint8Array | null,
		newFile: File,
		category: DocumentCategory,
		onProgress?: (progress: number) => void
	) => {
		// Validate individual file size
		const fileValidation = validateFileSize(newFile.size, newFile.name);
		if (!fileValidation.isValid) {
			handleError(toast, undefined, "File Size Error", fileValidation.error!);
			throw new Error(fileValidation.error!);
		}

		// Validate total claim size
		const claimValidation = validateClaimSize(claim.documents, newFile.size);
		if (!claimValidation.isValid) {
			handleError(toast, undefined, "Claim Size Error", claimValidation.error!);
			throw new Error(claimValidation.error!);
		}

		// Set the current file being uploaded
		setCurrentUploadFile(newFile.name);
		setUploadProgress(0);
		if (claim.claimId) {
			try {
				// If bytes are not provided, use the createCoreFile function to read the file
				let fileBytes: Uint8Array;
				if (bytes) {
					fileBytes = bytes;
				} else {
					// Read the file without progress tracking since we'll track upload progress instead
					const coreFile = await createCoreFile(newFile);
					fileBytes = coreFile.getContent_asU8();
				}
				// Set loading state for the API call
				setIsLoading(true);
				// Chunk size of 5MB
				const CHUNK_SIZE = 5 * 1024 * 1024;
				const totalChunks = Math.ceil(fileBytes.length / CHUNK_SIZE);
				let uploadId: string | undefined;

				// Upload chunks
				for (let i = 0; i < totalChunks; i++) {
					const start = i * CHUNK_SIZE;
					const end = Math.min(start + CHUNK_SIZE, fileBytes.length);
					const chunk = fileBytes.slice(start, end);
					const isLastChunk = i === totalChunks - 1;
					const sequenceNumber = i + 1; // Start from 1 instead of 0

					// Update progress based on chunk upload
					const chunkProgress = Math.round(((i + 1) / totalChunks) * 100);
					if (onProgress) {
						onProgress(chunkProgress);
					}
					setUploadProgress(chunkProgress);

					// Upload chunk
					const response = await postAssociateDocsChunk(
						claim.claimId,
						chunk,
						newFile,
						category,
						sequenceNumber,
						isLastChunk,
						uploadId
					);

					// Store upload ID from first chunk for subsequent chunks
					if (i === 0) {
						uploadId = response.getId();
					}
				}

				// Refresh the claim data, preserving unsaved local billing edits
				await handleFetchClaim(claim.claimId, {
					preserveFields: {
						billingtotal: localClaim.billingtotal,
						billingAdditionalFees: localClaim.billingAdditionalFees,
						claimServices: localClaim.claimServices,
						otherNonCoveredFees: localClaim.otherNonCoveredFees,
						otherNonCoveredHours: localClaim.otherNonCoveredHours
					}
				});
			} catch (error) {
				// Don't show toast here - let the component handle it with filename context
				// Just log the error and rethrow with clean message
				console.error("Error associating document:", error);

				if ((error as RpcError)?.code === StatusCode.ALREADY_EXISTS) {
					throw new Error("A document has already been uploaded with that same name");
				} else {
					const cleanMessage = getCleanErrorMessage(error as Error);
					throw new Error(cleanMessage);
				}
			} finally {
				// Reset progress state
				setUploadProgress(null);
				setCurrentUploadFile(null);
				setIsLoading(false);
			}
		} else {
			// Reset progress state
			setUploadProgress(null);
			setCurrentUploadFile(null);
			handleError(toast, undefined, "Claim ID is not set", "Error associating document because Claim ID is not set");
			// Throw a new error so the calling code knows the operation failed
			throw new Error("Claim ID is not set");
		}
	};

	const handleDocumentDisassociation = async (fileId: string) => {
		setIsLoading(true);
		if (claim.claimId) {
			try {
				await postDisassociateDocs(claim.claimId, fileId);
				await handleFetchClaim(claim.claimId);
			} catch (error) {
				const rpcError = error as RpcError;
				handleError(toast, rpcError, rpcError.message, rpcError.toString());
			} finally {
				setIsLoading(false);
			}
		} else {
			handleError(
				toast,
				undefined,
				"Claim ID is not set",
				"Attempted to remove a Document associated to a Claim with no ID!"
			);
			setIsLoading(false);
		}
	};

	const handleRemoveDocument = async (claimId: number, fileId: string): Promise<RemoveDocumentResult> => {
		// Check if the operation is already in progress
		if (pendingDeletions.has(fileId)) {
			return {
				error: {
					message: "Operation in progress. Please wait for the current operation to complete."
				}
			};
		}

		// Basic validation
		if (!claimId) {
			console.error("Document removal failed: Missing claim ID");
			return { error: { message: "Claim ID is missing" } };
		}

		if (!fileId) {
			console.error("Document removal failed: Missing file ID");
			return { error: { message: "File ID is missing" } };
		}

		// Check claim status
		if (
			claim.status !== ClaimStatus.Draft &&
			claim.status !== ClaimStatus.InReview &&
			claim.status !== ClaimStatus.PendingVerification &&
			claim.status !== ClaimStatus.PendingProvider &&
			claim.status !== ClaimStatus.PendingInternalReview
		) {
			console.error(`Document modification not allowed for claim status: ${claim.status}`);
			return {
				error: {
					code: StatusCode.FAILED_PRECONDITION,
					message: `Documents can only be modified for claims in supported statuses. This claim is in ${claim.status} status.`
				}
			};
		}

		// Find the document to remove
		const doc = claim.documents.find((d) => d.file?.fileId === fileId);
		if (!doc) {
			console.error(`Document with ID ${fileId} not found in claim data`);
			return {
				error: {
					code: StatusCode.NOT_FOUND,
					message: "Document not found in current claim data"
				}
			};
		}

		// Track this operation as pending
		setPendingDeletions((prev) => new Set([...prev, fileId]));

		// Store original state to revert if needed
		const originalClaimState = { ...claim };

		// Update UI optimistically
		const updatedDocuments = claim.documents.map((d) => (d.file?.fileId === fileId ? { ...d, isDeleted: true } : d));

		setClaim({
			...claim,
			documents: updatedDocuments
		});

		try {
			// Make the API call
			await removeDocument(claimId, fileId);
			return { success: true };
		} catch (error) {
			// Revert the optimistic update
			setClaim(originalClaimState);

			// Extract error details
			const rpcError = error as RpcError;
			console.error("Error removing document:", {
				error: rpcError,
				code: rpcError?.code,
				message: rpcError?.message,
				fileId,
				claimId
			});

			// Return a structured error response
			return {
				error: {
					code: rpcError?.code,
					message: rpcError?.message || "An unexpected error occurred"
				}
			};
		} finally {
			// Always clear the pending state
			setPendingDeletions((prev) => {
				const newSet = new Set([...prev]);
				newSet.delete(fileId);
				return newSet;
			});
		}
	};

	const handleRecoverDocument = async (claimId: number, fileId: string): Promise<RecoverDocumentResult> => {
		// Check if the operation is already in progress
		if (pendingDeletions.has(fileId)) {
			return {
				error: {
					message: "Operation in progress. Please wait for the current operation to complete."
				}
			};
		}

		// Basic validation
		if (!claimId) {
			console.error("Document recovery failed: Missing claim ID");
			return { error: { message: "Claim ID is missing" } };
		}

		if (!fileId) {
			console.error("Document recovery failed: Missing file ID");
			return { error: { message: "File ID is missing" } };
		}

		// Check claim status
		if (
			claim.status !== ClaimStatus.Draft &&
			claim.status !== ClaimStatus.InReview &&
			claim.status !== ClaimStatus.PendingVerification &&
			claim.status !== ClaimStatus.PendingProvider &&
			claim.status !== ClaimStatus.PendingInternalReview
		) {
			console.error(`Document modification not allowed for claim status: ${claim.status}`);
			return {
				error: {
					code: StatusCode.FAILED_PRECONDITION,
					message: `Documents can only be modified for claims in supported statuses. This claim is in ${claim.status} status.`
				}
			};
		}

		// Find the document to recover
		const doc = claim.documents.find((d) => d.file?.fileId === fileId);
		if (!doc) {
			console.error(`Document with ID ${fileId} not found in claim data`);
			return {
				error: {
					code: StatusCode.NOT_FOUND,
					message: "Document not found in current claim data"
				}
			};
		}

		// Track this operation as pending
		setPendingDeletions((prev) => new Set([...prev, fileId]));

		// Store original state to revert if needed
		const originalClaimState = { ...claim };

		// Update UI optimistically
		const updatedDocuments = claim.documents.map((d) => (d.file?.fileId === fileId ? { ...d, isDeleted: false } : d));

		setClaim({
			...claim,
			documents: updatedDocuments
		});

		try {
			// Make the API call
			await recoverDocument(claimId, fileId);
			return { success: true };
		} catch (error) {
			// Revert the optimistic update
			setClaim(originalClaimState);

			// Extract error details
			const rpcError = error as RpcError;
			console.error("Error recovering document:", {
				error: rpcError,
				code: rpcError?.code,
				message: rpcError?.message,
				fileId,
				claimId
			});

			// Return a structured error response
			return {
				error: {
					code: rpcError?.code,
					message: rpcError?.message || "An unexpected error occurred"
				}
			};
		} finally {
			// Always clear the pending state
			setPendingDeletions((prev) => {
				const newSet = new Set([...prev]);
				newSet.delete(fileId);
				return newSet;
			});
		}
	};

	const handlePermanentlyRemoveDocument = async (
		claimId: number,
		fileId: string
	): Promise<PermanentlyRemoveDocumentResult> => {
		// Check if the operation is already in progress
		if (pendingDeletions.has(fileId)) {
			return {
				error: {
					message: "Operation in progress. Please wait for the current operation to complete."
				}
			};
		}

		// Basic validation
		if (!claimId) {
			console.error("Document permanent removal failed: Missing claim ID");
			return { error: { message: "Claim ID is missing" } };
		}

		if (!fileId) {
			console.error("Document permanent removal failed: Missing file ID");
			return { error: { message: "File ID is missing" } };
		}

		// Find the document to permanently remove
		const doc = claim.documents.find((d) => d.file?.fileId === fileId);
		if (!doc) {
			console.error(`Document with ID ${fileId} not found in claim data`);
			return {
				error: {
					code: StatusCode.NOT_FOUND,
					message: "Document not found in current claim data"
				}
			};
		}

		// Track this operation as pending
		setPendingDeletions((prev) => new Set([...prev, fileId]));

		// Store original state to revert if needed
		const originalClaimState = { ...claim };

		// Update UI optimistically - remove the document from the list
		const updatedDocuments = claim.documents.filter((d) => d.file?.fileId !== fileId);

		setClaim({
			...claim,
			documents: updatedDocuments
		});

		try {
			// Make the API call
			await permanentlyRemoveDocument(claimId, fileId);
			return { success: true };
		} catch (error) {
			// Revert the optimistic update
			setClaim(originalClaimState);

			// Extract error details
			const rpcError = error as RpcError;
			console.error("Error permanently removing document:", {
				error: rpcError,
				code: rpcError?.code,
				message: rpcError?.message,
				fileId,
				claimId
			});

			// Return a structured error response
			return {
				error: {
					code: rpcError?.code,
					message: rpcError?.message || "An unexpected error occurred"
				}
			};
		} finally {
			// Always clear the pending state
			setPendingDeletions((prev) => {
				const newSet = new Set([...prev]);
				newSet.delete(fileId);
				return newSet;
			});
		}
	};

	const getServiceLabel = (claimService: any): string => {
		return getServiceLabelFn(benefits, claimService);
	};

	const handleSubmitClaim = async (receivedOn: Date | undefined) => {
		setIsLoading(true);
		if (claim.claimId) {
			try {
				await postSubmitClaim(claim.claimId, receivedOn);
				await handleFetchClaim(claim.claimId);
			} catch (error) {
				handleError(toast, error as RpcError, "Failed to submit claim", "Error submitting claim");
			} finally {
				setIsLoading(false);
			}
		} else {
			handleError(toast, undefined, "Claim ID is not set", "Error associating document because Claim ID is not set");
			setIsLoading(false);
		}
	};

	const handleFetchCase = async (caseId: number): Promise<Case | undefined> => {
		setIsLoading(true);
		try {
			const fetchedCase = await handleFetchCaseUtil(caseId, getCase);
			if (fetchedCase) {
				setClaimCase(fetchedCase);
			} else {
				toast("Error", "Unable to fetch Case associated to this Claim", "error");
			}
			return fetchedCase;
		} catch (error) {
			console.error(error);
			toast("Error", (error as RpcError).message, "error");
			return undefined;
		} finally {
			setIsLoading(false);
		}
	};

	const handleFetchClaimsLampCase = async (caseId: number): Promise<ClaimsLampCase | undefined> => {
		setIsLoading(true);
		try {
			const result = await getClaimsLampCase(caseId);
			const lampCase = result.getCase();
			return lampCase;
		} catch (error) {
			console.error(error);
			toast("Error", (error as RpcError).message, "error");
			return undefined;
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<ClaimContext.Provider
			value={{
				claim,
				localClaim,
				claimCase,
				benefits,
				localBenefits,
				benefitsList,
				isLoading,
				lgPolicyId,
				pendingDeletions,
				billHourlyAsFlat,
				billFlatAsHourly,
				doesLegalMatterExist,
				ccsGenerationStatus,
				ccsGenerationError,
				setIsLoading,
				setLgPolicyId,
				setClaim,
				setLocalClaim,
				setBillHourlyAsFlat,
				setBillFlatAsHourly,
				setCcsGenerationStatus,
				setCcsGenerationError,
				handleFetchClaim,
				handleClaimUpsert,
				handleDocumentAssociation,
				handleDocumentDisassociation,
				handleSubmitClaim,
				handleServiceDelete,
				handleFetchCase,
				getServiceLabel,
				handleRemoveDocument,
				handleRecoverDocument,
				handlePermanentlyRemoveDocument
			}}>
			{children}
		</ClaimContext.Provider>
	);
};

export const useClaim = () => {
	const context = useContext(ClaimContext);
	if (!context) throw new Error("useClaim must be used within a ClaimProvider");
	return context;
};
